import { chain } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { DomainAnalysisGq } from '../../../graphql/projects/queries/analysis/useGetDomainTraffic';
import { useDebounceValue } from '../../../logic/otherUtilities/useDebounceValue';
import { AnalysisFilterState } from './analysisFilterState';

export const useAnalysisFilterState = (project: ProjectGq, domainAnalyses?: DomainAnalysisGq[]) => {
  // Stored filter logic
  const analysis_filter_version = 1;

  const relevantDomains = project.domains.filter(d => !!project.useAutomaticDomains || d.type !== DomainTypeGq.AUTOMATIC);
  const analysis_filter = useMemo((): AnalysisFilterState => {
    const initial_filter: AnalysisFilterState = {
      language: 'en',
      multiTagGroupNames: [],
      startMonthIncrement: 1,
      shownFields: {},
      serpOnly: false,
      tagGroupIds: project.tagGroups.map((tg) => tg.id).slice(0, 7),
      domainIds: relevantDomains.map((d) => d.id).slice(0, 10),
      limitItems: 7,
      tagGroupId: -1,
      tagId: -1,
      hBLtValues: [2, 3],
      targetRankValues: [1, 2, 3],
      baselineRankValues: [1],
      conversionRate: '2',
      averageOrderValue: '1000',
      rankSumIntervals: '0',
      currentResult: true,
      competitorColor: false,
      years: 3,
      theme: 'voxel',
      rampUpTime: 12,
    };

    const stored_analysis_filter_version = sessionStorage.getItem('analysis_filter_version');
    if (+stored_analysis_filter_version !== analysis_filter_version) {
      return initial_filter;
    }

    const stored_filter: AnalysisFilterState = JSON.parse(
      sessionStorage.getItem('analysis_filter_' + project.id)
    );
    if (!stored_filter) {
      return initial_filter;
    }



    const filter = {
      ...initial_filter,
      ...stored_filter,
      // Clean up stored filter - in case tag groups/domains don't longer exist
      tagGroupIds:
        stored_filter.tagGroupIds?.filter((tagGroupId) =>
          project.tagGroups.some((tg) => tg.id === tagGroupId)
        ) ?? initial_filter.tagGroupIds,
      tagGroupId: project.tagGroups.some((tg) => tg.id === stored_filter?.tagGroupId)
        ? stored_filter.tagGroupId
        : -1,
      tagId: project.tagGroups
        .flatMap((tg) => tg.tags.map((t) => t.id))
        .some((tId) => tId === stored_filter?.tagId)
        ? stored_filter.tagId
        : -1,

      // Clean up domains if 1.) domain no longer exist or 2.) the "include automatic domain" setting has changed
      domainIds:
        stored_filter.domainIds?.filter((domainId) => relevantDomains.some((domain) => domain.id === domainId))
        ?? initial_filter.domainIds,

    };

    if (!filter.averageOrderValue?.length) filter.averageOrderValue = initial_filter.averageOrderValue;
    if (!filter.conversionRate?.length) filter.conversionRate = initial_filter.conversionRate;

    return filter;
  }, [project.tagGroups, project.domains, project.useAutomaticDomains]);

  const [analysisState, setAnalysisState] = useState<AnalysisFilterState>(analysis_filter);

  // Set the first domain if there's no analysis filter state
  useEffect(() => {
    if (domainAnalyses?.length) {
      let projectDomain = project.domains?.find((d) => d.type === DomainTypeGq.PROJECT)?.hostName;

      setAnalysisState((prev) => {
        const stored_filter: AnalysisFilterState = JSON.parse(
          sessionStorage.getItem('analysis_filter_' + project.id)
        );

        const domains = !!stored_filter?.domainIds?.length
          // This is just falling back on the previous setting
          ? prev.domainIds
          // This is like setting the first domain suggestion
          : chain(domainAnalyses)
            .filter((d) => {

              // Don't include if project domain
              if (d.domain.type === DomainTypeGq.PROJECT) {
                return false;
              }

              // Don't include if it seems to match the project domain
              if (
                projectDomain &&
                (projectDomain.toLowerCase().includes(d.domain.hostName.toLowerCase()) ||
                  d.domain.hostName.toLowerCase().includes(projectDomain.toLowerCase()))
              ) {
                return false;
              }

              // Automatic domain filter
              if (!!project.useAutomaticDomains && d.domain.type === DomainTypeGq.AUTOMATIC) {
                return false;
              }

              // Otherwise include
              return true;
            })
            .orderBy((d) => d.traffic, ['desc'])
            .slice(0, 10)
            .map((d) => d.domain.id)
            .value();

        return { ...prev, domainIds: domains };
      });
    }
  }, [domainAnalyses, project.domains]);

  const analysisStateInitialized = !!domainAnalyses;

  // Update stored filter
  const debounced_filter = useDebounceValue(analysisState, 300);
  useEffect(() => {
    sessionStorage.setItem('analysis_filter_version', analysis_filter_version.toString());
    sessionStorage.setItem('analysis_filter_' + project.id, JSON.stringify(analysisState));
  }, [debounced_filter]);

  return { analysisState, setAnalysisState, analysisStateInitialized };
};
